import React, { useState, useEffect, useRef } from 'react';
import { StyleSheet, Text, View, Platform, TextInput, TouchableOpacity, useWindowDimensions } from 'react-native';
import { Camera, useCameraDevice, useCameraPermission, useCodeScanner, useCameraFormat } from 'react-native-vision-camera';

interface QRScannerProps {
    onScan: (data: string) => void;
    onCancel: () => void;
}

/**
 * Normalize a manually-entered class code into the canonical `AAA-123` format.
 * Accepts input with or without the hyphen, in any case, with stray whitespace.
 * Returns the input as uppercase with a hyphen inserted between the 3-letter
 * prefix and the 3-digit suffix, preserving partial input while typing.
 */
export const normalizeClassCode = (raw: string): string => {
    // Strip whitespace and hyphens, uppercase everything
    const cleaned = raw.replace(/[\s-]/g, '').toUpperCase();

    // Split into alpha prefix (max 3) and numeric suffix (max 3)
    const letters = cleaned.slice(0, 3).replace(/[^A-Z]/g, '');
    const digits = cleaned.slice(letters.length, letters.length + 3).replace(/[^0-9]/g, '');

    if (digits.length === 0) return letters;
    return `${letters}-${digits}`;
};

export const QRScanner: React.FC<QRScannerProps> = ({ onScan, onCancel }) => {
    const [manualCode, setManualCode] = useState('');
    const [showManual, setShowManual] = useState(false);
    useWindowDimensions();
    const [layout, setLayout] = useState({ width: 0, height: 0 });
    const isLandscape = layout.width > layout.height;

    const { hasPermission, requestPermission } = Platform.OS !== 'web'
        ? useCameraPermission()
        : { hasPermission: true, requestPermission: async () => true };

    const device = Platform.OS !== 'web' ? useCameraDevice('back') : undefined;
    const [scanned, setScanned] = useState(false);
    const [zoom, setZoom] = useState(1);
    const cameraRef = useRef<Camera>(null);

    // Layout Debug Modes - hidden but preserved for future use
    const layoutMode = 0;
    const layouts = [
        { name: 'DEFAULT', width: '100%', height: '100%', left: 0, top: 0 },
        { name: 'WIDE OVERFLOW', width: '150%', height: '100%', left: '-25%', top: 0 },
        { name: 'HEIGHT PADDING', width: '100%', height: '75%', left: 0, top: '12.5%' },
    ];
    const currentLayout = layouts[layoutMode % layouts.length];

    // Select a format that is stable and fast for scanning (720p is better than 1080p for scanner performance)
    const format = useCameraFormat(device, [
        { videoResolution: { width: 1280, height: 720 } },
        { videoResolution: { width: 960, height: 540 } },
        { videoAspectRatio: 16 / 9 },
        { fps: 25 }
    ]);

    // Initial setup
    useEffect(() => {
        if (device?.neutralZoom) {
            setZoom(device.neutralZoom);
        }
    }, [device]);

    const toggleZoom = () => {
        const neutral = device?.neutralZoom ?? 1;
        const max = Math.min(device?.maxZoom ?? 2, 4);
        setZoom(prev => prev > neutral ? neutral : Math.min(max, neutral * 2));
    };

    const codeScanner = Platform.OS !== 'web' ? useCodeScanner({
        codeTypes: ['qr'], // Restrict to QR only to prevent false positives from barcodes/text
        onCodeScanned: (codes) => {
            if (scanned || codes.length === 0) return;

            const code = codes[0];
            if (code.value) {
                console.log('[QRScanner] Scanned QR:', code.value);
                setScanned(true);
                onScan(code.value);
            }
        }
    }) : undefined;

    useEffect(() => {
        if (scanned || showManual || !device) return;

        // Auto-Hunt Logic: Cycle zoom levels to find the QR code
        // We slowed this down to 10s to prevent session strain/freezing on older hardware
        const huntTimeout = setTimeout(() => {
            const neutral = device.neutralZoom ?? 1;
            const max = device.maxZoom ?? 4;

            setZoom(prev => {
                const level2 = Math.min(neutral * 2, max);
                const level3 = Math.min(neutral * 3.5, max);

                if (prev < level2) return level2;
                if (prev < level3) return level3;
                return neutral; // Reset back to wide
            });

            console.log(`[QRScanner] Auto-Hunt: Cycling zoom level`);
        }, 10000);

        return () => clearTimeout(huntTimeout);
    }, [zoom, scanned, showManual, device]);

    useEffect(() => {
        if (Platform.OS !== 'web' && !hasPermission) {
            requestPermission();
        }
    }, [hasPermission, requestPermission]);

    // Shared manual entry UI for Web or when 'showManual' is toggled on native
    if (Platform.OS === 'web') {
        // eslint-disable-next-line @typescript-eslint/no-require-imports -- intentional dynamic require for web-only component to avoid loading @zxing/library on native
        const { WebQRScanner } = require('./WebQRScanner');
        return <WebQRScanner onScan={onScan} onCancel={onCancel} />;
    }

    if (showManual) {
        return (
            <View style={styles.container}>
                <View style={styles.webContainer}>
                    <Text style={styles.webTitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Manual Entry</Text>
                    <Text style={styles.text} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Enter Class Code manually:</Text>
                    <TextInput
                        style={styles.input}
                        placeholder="e.g. ABC-123"
                        placeholderTextColor="#94A3B8"
                        value={manualCode}
                        onChangeText={(text) => setManualCode(normalizeClassCode(text))}
                        autoCapitalize="characters"
                        autoCorrect={false}
                        maxLength={7}
                        accessibilityLabel="Class code input"
                        accessibilityHint="Type your class code here"
                    />
                    <TouchableOpacity
                        style={[styles.button, { backgroundColor: '#22c55e' }]}
                        onPress={() => onScan(normalizeClassCode(manualCode))}
                        accessibilityRole="button"
                        accessibilityLabel="Go"
                        accessibilityHint="Double tap to join the class with this code"
                    >
                        <Text style={styles.buttonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>GO! ➔</Text>
                    </TouchableOpacity>

                    <View style={{ height: 12 }} />

                    <TouchableOpacity
                        style={[styles.button, { backgroundColor: '#F1F5F9', borderWidth: 1, borderColor: '#CBD5E1' }]}
                        onPress={() => setShowManual(false)}
                        accessibilityRole="button"
                        accessibilityLabel="Back to Scanner"
                        accessibilityHint="Double tap to go back to the QR scanner"
                    >
                        <Text style={[styles.buttonText, { color: '#64748B' }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                            Back to Scanner
                        </Text>
                    </TouchableOpacity>
                </View>
            </View>
        );
    }

    if (!hasPermission) {
        return (
            <View style={styles.container}>
                <Text style={styles.text} allowFontScaling={true} maxFontSizeMultiplier={1.5}>We need your permission to show the camera</Text>
                <TouchableOpacity style={[styles.button, { backgroundColor: '#22c55e', alignSelf: 'center', width: '60%', marginTop: 20 }]} onPress={requestPermission} accessibilityRole="button" accessibilityLabel="OK" accessibilityHint="Double tap to grant camera permission">
                    <Text style={styles.buttonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>OK! ➔</Text>
                </TouchableOpacity>
            </View>
        );
    }

    if (device == null) {
        return (
            <View style={styles.container}>
                <Text style={styles.text} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Loading Camera...</Text>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <View
                style={styles.cameraWrapper}
                accessibilityLabel="QR code scanner"
                accessibilityHint="Point the camera at a QR code to scan it"
                onLayout={(e) => {
                    const { width, height } = e.nativeEvent.layout;
                    // Rounding dimensions to prevent jitter-induced camera restarts
                    setLayout({ width: Math.round(width), height: Math.round(height) });
                }}
            >
                {layout.width > 0 && (
                    <Camera
                        key={`${device.id}-${currentLayout.name}`}
                        ref={cameraRef}
                        style={({
                            position: 'absolute',
                            width: currentLayout.width as unknown,
                            height: currentLayout.height as unknown,
                            left: currentLayout.left as unknown,
                            top: currentLayout.top as unknown,
                          } as any)}
                        device={device}
                        format={format}
                        isActive={!scanned && !showManual}
                        codeScanner={codeScanner}
                        resizeMode="cover"
                        zoom={zoom}
                    />
                )}


                {zoom > (device?.neutralZoom ?? 1) && !scanned && !showManual && (
                    <View style={styles.huntBadge}>
                        <Text style={styles.huntText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Enhancing View...</Text>
                    </View>
                )}

                <View style={[StyleSheet.absoluteFillObject, styles.overlay]} pointerEvents="box-none">
                    <View style={[
                        styles.scanWindow,
                        isLandscape && styles.scanWindowLandscape
                    ]} pointerEvents="none" />

                    <View style={styles.controlsColumn}>
                        <TouchableOpacity
                            style={[styles.controlButton, { backgroundColor: 'rgba(255,255,255,0.2)' }]}
                            onPress={() => setShowManual(true)}
                            accessibilityRole="button"
                            accessibilityLabel="Enter code manually"
                            accessibilityHint="Double tap to type the class code instead of scanning"
                        >
                            <Text style={styles.controlButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Code</Text>
                        </TouchableOpacity>

                        <View style={{ height: 16 }} />

                        <TouchableOpacity
                            style={[styles.controlButton, { backgroundColor: 'rgba(255,255,255,0.2)' }]}
                            onPress={toggleZoom}
                            accessibilityRole="button"
                            accessibilityLabel="Zoom"
                            accessibilityHint="Double tap to zoom in or out"
                        >
                            <Text style={styles.controlButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Zoom</Text>
                        </TouchableOpacity>

                        <View style={{ height: 16 }} />

                        <TouchableOpacity style={styles.controlButton} onPress={onCancel} accessibilityRole="button" accessibilityLabel="Cancel" accessibilityHint="Double tap to cancel scanning">
                            <Text style={styles.controlButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Cancel</Text>
                        </TouchableOpacity>
                    </View>
                </View>

                {/* Build version indicator for verification */}
                <View style={styles.buildBadge}>
                    <Text style={styles.buildText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>v1.0.25-simplified</Text>
                </View>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    cameraWrapper: {
        flex: 1,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
    },
    webContainer: {
        padding: 40,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#FFFFFF',
        marginHorizontal: 20,
        marginTop: 10,
        borderRadius: 20,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 10,
        elevation: 5,
    },
    webTitle: {
        fontSize: 24,
        fontWeight: 'bold',
        marginBottom: 8,
        color: '#0F172A',
    },
    input: {
        width: '100%',
        height: 56,
        borderWidth: 2,
        borderColor: '#E2E8F0',
        borderRadius: 12,
        paddingHorizontal: 16,
        fontSize: 20,
        fontWeight: '600',
        letterSpacing: 1,
        color: '#1E293B',
        backgroundColor: '#F8FAFC',
        marginVertical: 20,
        textAlign: 'center',
    },
    button: {
        width: '100%',
        height: 52,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
    },
    buttonText: {
        color: '#FFFFFF',
        fontSize: 20,
        fontWeight: '900',
    },
    overlay: {
        flex: 1,
        backgroundColor: 'transparent',
        alignItems: 'center',
        justifyContent: 'center',
    },
    scanWindow: {
        width: 250,
        height: 250,
        borderWidth: 4,
        borderColor: '#22c55e',
        borderRadius: 20,
        backgroundColor: 'transparent',
        marginBottom: 40,
    },
    scanWindowLandscape: {
        width: 180,
        height: 180,
        marginBottom: 20,
    },
    controlsColumn: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        paddingHorizontal: 40,
        marginTop: 20,
    },
    controlButton: {
        width: '100%',
        height: 64,
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.3)',
    },
    controlButtonText: {
        color: '#FFFFFF',
        fontSize: 20,
        fontWeight: 'bold',
    },
    text: {
        textAlign: 'center',
        fontSize: 16,
        color: '#64748B',
    },
    hintText: {
        color: '#FFFFFF',
        fontSize: 14,
        marginTop: 20,
        backgroundColor: 'rgba(0,0,0,0.4)',
        paddingHorizontal: 12,
        paddingVertical: 5,
        borderRadius: 10,
    },
    buildBadge: {
        position: 'absolute',
        bottom: 10,
        right: 10,
        backgroundColor: 'rgba(0,0,0,0.5)',
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 5,
    },
    buildText: {
        color: 'rgba(255,255,255,0.6)',
        fontSize: 10,
        fontWeight: 'bold',
        fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    },
    huntBadge: {
        position: 'absolute',
        top: 50,
        alignSelf: 'center',
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        paddingHorizontal: 16,
        paddingVertical: 6,
        borderRadius: 20,
        zIndex: 10,
    },
    huntText: {
        color: '#FFFFFF',
        fontSize: 12,
        fontWeight: 'bold',
    },
});
